// client/src/components/DebtPayoff/DebtPayoffPage.js
/**
 * DebtPayoffPage — F4 Debt Payoff Planner
 * Sections:
 *   1. Debt overview cards
 *   2. Payoff planner (Avalanche / Snowball, extra payment, schedule chart)
 *   3. Collapsible strategy comparison table
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  TextField,
  InputAdornment,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Skeleton,
  Alert,
  Collapse,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  Stack,
  Divider,
  Paper,
} from '@mui/material';
import {
  AccountBalance as LoanIcon,
  CreditCard as CreditCardIcon,
  Home as MortgageIcon,
  InfoOutlined as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  TrendingDown as TrendingDownIcon,
  EmojiEvents as TrophyIcon,
  Calculate as CalcIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import api from '../../services/api';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n) =>
  `€ ${Number(n || 0).toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
};

const TYPE_META = {
  loan:       { label: 'Loan',        color: 'warning', icon: <LoanIcon fontSize="small" /> },
  mortgage:   { label: 'Mortgage',    color: 'info',    icon: <MortgageIcon fontSize="small" /> },
  creditcard: { label: 'Credit Card', color: 'error',   icon: <CreditCardIcon fontSize="small" /> },
  credit:     { label: 'Credit',      color: 'error',   icon: <CreditCardIcon fontSize="small" /> },
};

const getMeta = (type) => TYPE_META[type] || { label: type, color: 'default', icon: <LoanIcon fontSize="small" /> };

// ─── Debt overview card ───────────────────────────────────────────────────────

const DebtCard = ({ debt }) => {
  const meta     = getMeta(debt.type);
  const original = debt.originalAmount || debt.initialBalance || debt.currentBalance || 1;
  const paid     = Math.max(0, original - (debt.currentBalance || 0));
  const pct      = Math.min(100, Math.round((paid / original) * 100));

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderRadius: 3,
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: 4 },
      }}
    >
      <CardContent>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ color: `${meta.color}.main` }}>{meta.icon}</Box>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {debt.name}
            </Typography>
          </Stack>
          <Chip label={meta.label} color={meta.color} size="small" />
        </Stack>

        {/* Balance */}
        <Typography variant="h5" fontWeight={800} color="error.main" gutterBottom>
          {fmt(debt.currentBalance)}
        </Typography>

        {/* Details grid */}
        <Grid container spacing={1} mb={1.5}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Interest rate
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {debt.interestRate != null ? `${debt.interestRate}% APR` : '—'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Min. payment
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {debt.minimumPayment != null ? fmt(debt.minimumPayment) + '/mo' : '—'}
            </Typography>
          </Grid>
          {debt.dueDate && (
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Due day
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                Day {debt.dueDate}
              </Typography>
            </Grid>
          )}
          {debt.institution && (
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Institution
              </Typography>
              <Typography variant="body2" fontWeight={600} noWrap>
                {debt.institution}
              </Typography>
            </Grid>
          )}
        </Grid>

        {/* Progress */}
        <Box>
          <Stack direction="row" justifyContent="space-between" mb={0.5}>
            <Typography variant="caption" color="text.secondary">
              Paid off
            </Typography>
            <Typography variant="caption" fontWeight={700} color="success.main">
              {pct}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={pct}
            color="success"
            sx={{ height: 8, borderRadius: 4 }}
          />
          {debt.originalAmount && (
            <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
              Original: {fmt(debt.originalAmount)}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// ─── Summary stat card ────────────────────────────────────────────────────────

const StatCard = ({ label, value, subLabel, color = 'primary', icon }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: 3,
      border: '1px solid',
      borderColor: 'divider',
      height: '100%',
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
      {icon && <Box sx={{ color: `${color}.main` }}>{icon}</Box>}
      <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
        {label}
      </Typography>
    </Stack>
    <Typography variant="h5" fontWeight={800} color={`${color}.main`}>
      {value}
    </Typography>
    {subLabel && (
      <Typography variant="caption" color="text.secondary">
        {subLabel}
      </Typography>
    )}
  </Paper>
);

// ─── Skeleton loading ─────────────────────────────────────────────────────────

const DebtCardSkeleton = () => (
  <Card variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
    <Skeleton variant="text" width="60%" height={28} />
    <Skeleton variant="text" width="40%" height={40} />
    <Skeleton variant="rectangular" height={60} sx={{ mt: 1, borderRadius: 1 }} />
    <Skeleton variant="rectangular" height={8} sx={{ mt: 2, borderRadius: 4 }} />
  </Card>
);

// ─── Main page ────────────────────────────────────────────────────────────────

const DebtPayoffPage = () => {
  // Debts state
  const [debts, setDebts]         = useState([]);
  const [totalDebt, setTotalDebt] = useState(0);
  const [hasDebts, setHasDebts]   = useState(false);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  // Planner inputs
  const [extra, setExtra]         = useState('');
  const [method, setMethod]       = useState('avalanche');

  // Plan result
  const [plan, setPlan]                   = useState(null);
  const [planLoading, setPlanLoading]     = useState(false);
  const [planError, setPlanError]         = useState(null);

  // Comparison
  const [compareOpen, setCompareOpen]     = useState(false);
  const [comparison, setComparison]       = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareError, setCompareError]   = useState(null);

  // ── Fetch debts ──────────────────────────────────────────────────────────────
  const fetchDebts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/debt-payoff');
      setHasDebts(res.data.hasDebts || false);
      setDebts(res.data.debts || []);
      setTotalDebt(res.data.totalDebt || 0);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load debt accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDebts(); }, [fetchDebts]);

  // ── Calculate plan ───────────────────────────────────────────────────────────
  const handleCalculate = async () => {
    setPlanLoading(true);
    setPlanError(null);
    setPlan(null);
    try {
      const extraVal = parseFloat(extra) || 0;
      const res = await api.get('/debt-payoff/plan', {
        params: { method, extra: extraVal },
      });
      setPlan(res.data.plan || null);
    } catch (err) {
      setPlanError(err.response?.data?.message || 'Failed to compute plan');
    } finally {
      setPlanLoading(false);
    }
  };

  // ── Compare strategies ───────────────────────────────────────────────────────
  const handleCompare = async () => {
    if (compareOpen) {
      setCompareOpen(false);
      return;
    }
    setCompareOpen(true);
    if (comparison) return; // already fetched
    setCompareLoading(true);
    setCompareError(null);
    try {
      const extraVal = parseFloat(extra) || 0;
      const res = await api.get('/debt-payoff/compare', {
        params: { extra: extraVal },
      });
      setComparison(res.data);
    } catch (err) {
      setCompareError(err.response?.data?.message || 'Failed to compare strategies');
    } finally {
      setCompareLoading(false);
    }
  };

  // Reset comparison when extra changes so it gets re-fetched
  const handleExtraChange = (e) => {
    setExtra(e.target.value);
    setComparison(null);
    setCompareOpen(false);
    setPlan(null);
  };

  // ── Chart data ───────────────────────────────────────────────────────────────
  const chartData = plan
    ? Object.entries(plan.perDebt).map(([id, summary]) => ({
        name: summary.name,
        months: summary.payoffMonths,
        interest: summary.totalInterest,
      }))
    : [];

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>
      {/* ── Page header ── */}
      <Stack direction="row" spacing={2} alignItems="center" mb={1}>
        <TrendingDownIcon sx={{ fontSize: 36, color: 'error.main' }} />
        <Box>
          <Typography variant="h4" fontWeight={800}>
            Debt Payoff Planner
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Get debt-free faster with Avalanche or Snowball strategies
          </Typography>
        </Box>
      </Stack>

      <Divider sx={{ mb: 3 }} />

      {/* ─────────────────────────────────────────────────────────────────────── */}
      {/* SECTION 1 — Debt overview                                              */}
      {/* ─────────────────────────────────────────────────────────────────────── */}
      <Typography variant="h6" fontWeight={700} mb={2}>
        Your Debts
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Grid container spacing={2} mb={4}>
          {[1, 2, 3].map((n) => (
            <Grid item xs={12} sm={6} md={4} key={n}>
              <DebtCardSkeleton />
            </Grid>
          ))}
        </Grid>
      ) : !hasDebts ? (
        <Alert severity="info" sx={{ mb: 4 }}>
          No liability accounts found. Go to <strong>Accounts</strong> and add a{' '}
          <em>loan</em>, <em>mortgage</em>, or <em>credit card</em> account to start planning
          your debt payoff.
        </Alert>
      ) : (
        <>
          {/* Summary row */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={4}>
              <StatCard
                label="Total debt"
                value={fmt(totalDebt)}
                color="error"
                icon={<TrendingDownIcon />}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard
                label="Accounts"
                value={debts.length}
                subLabel="liability accounts"
                color="warning"
                icon={<LoanIcon />}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard
                label="Total min. payments"
                value={fmt(debts.reduce((s, d) => s + (d.minimumPayment || 0), 0))}
                subLabel="per month"
                color="primary"
                icon={<CalcIcon />}
              />
            </Grid>
          </Grid>

          {/* Debt cards */}
          <Grid container spacing={2} mb={4}>
            {debts.map((debt) => (
              <Grid item xs={12} sm={6} md={4} key={debt._id}>
                <DebtCard debt={debt} />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* ─────────────────────────────────────────────────────────────────────── */}
      {/* SECTION 2 — Payoff planner                                             */}
      {/* ─────────────────────────────────────────────────────────────────────── */}
      {hasDebts && !loading && (
        <>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="h6" fontWeight={700} mb={2}>
            Payoff Planner
          </Typography>

          <Paper
            elevation={0}
            sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 3 }}
          >
            <Grid container spacing={3} alignItems="flex-start">
              {/* Extra payment input */}
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Extra monthly payment"
                  type="number"
                  value={extra}
                  onChange={handleExtraChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">€</InputAdornment>,
                    inputProps: { min: 0, step: 10 },
                  }}
                  helperText="Amount beyond your minimum payments"
                  fullWidth
                  size="small"
                />
              </Grid>

              {/* Strategy selection */}
              <Grid item xs={12} sm={5}>
                <Typography variant="body2" color="text.secondary" mb={0.5} fontWeight={600}>
                  Strategy
                </Typography>
                <RadioGroup
                  row
                  value={method}
                  onChange={(e) => { setMethod(e.target.value); setPlan(null); }}
                >
                  <Tooltip
                    title="Avalanche: Pay the highest-interest debt first. Minimises total interest paid — the mathematically optimal strategy."
                    placement="top"
                    arrow
                  >
                    <FormControlLabel
                      value="avalanche"
                      control={<Radio size="small" color="error" />}
                      label={
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <span>Avalanche</span>
                          <InfoIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                        </Stack>
                      }
                    />
                  </Tooltip>
                  <Tooltip
                    title="Snowball: Pay the smallest balance first. Builds momentum by clearing debts quickly — great for motivation."
                    placement="top"
                    arrow
                  >
                    <FormControlLabel
                      value="snowball"
                      control={<Radio size="small" color="success" />}
                      label={
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <span>Snowball</span>
                          <InfoIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                        </Stack>
                      }
                    />
                  </Tooltip>
                </RadioGroup>
              </Grid>

              {/* Calculate button */}
              <Grid item xs={12} sm={3}>
                <Button
                  variant="contained"
                  color={method === 'avalanche' ? 'error' : 'success'}
                  fullWidth
                  onClick={handleCalculate}
                  disabled={planLoading}
                  startIcon={<CalcIcon />}
                  sx={{ mt: { xs: 0, sm: 3 } }}
                >
                  {planLoading ? 'Calculating…' : 'Calculate'}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Plan error */}
          {planError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPlanError(null)}>
              {planError}
            </Alert>
          )}

          {/* Plan results */}
          {planLoading && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 3 }}>
              <Skeleton variant="text" width="40%" height={32} />
              <Skeleton variant="rectangular" height={200} sx={{ mt: 2, borderRadius: 2 }} />
            </Paper>
          )}

          {plan && !planLoading && (
            <Paper
              elevation={0}
              sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 3 }}
            >
              {/* Summary stat cards */}
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} sm={4}>
                  <StatCard
                    label="Total interest"
                    value={fmt(plan.totalInterest)}
                    color="error"
                    icon={<TrendingDownIcon />}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <StatCard
                    label="Debt-free date"
                    value={fmtDate(plan.payoffDate)}
                    subLabel={`${plan.payoffMonths} months`}
                    color="success"
                    icon={<TrophyIcon />}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <StatCard
                    label="Interest saved vs min-only"
                    value={fmt(plan.interestSavedVsMinimum || 0)}
                    subLabel="by adding extra payment"
                    color="primary"
                    icon={<CalcIcon />}
                  />
                </Grid>
              </Grid>

              {/* Months-to-payoff chart */}
              {chartData.length > 0 && (
                <>
                  <Typography variant="subtitle2" fontWeight={700} mb={1}>
                    Months to pay off each debt
                  </Typography>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 12 }} label={{ value: 'Months', angle: -90, position: 'insideLeft', fontSize: 11 }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} tickFormatter={(v) => `€${(v / 1000).toFixed(1)}k`} />
                      <ReTooltip
                        formatter={(value, name) =>
                          name === 'interest' ? [fmt(value), 'Total interest'] : [value, 'Months to pay off']
                        }
                      />
                      <Legend />
                      <Bar yAxisId="left"  dataKey="months"   name="Months to pay off" fill="#ef5350" radius={[4, 4, 0, 0]} />
                      <Bar yAxisId="right" dataKey="interest" name="Total interest (€)" fill="#ff9800" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </>
              )}

              {/* Snowball first win */}
              {plan.firstWinDate && (
                <Alert severity="success" icon={<TrophyIcon />} sx={{ mt: 2 }}>
                  <strong>First debt paid off:</strong> {fmtDate(plan.firstWinDate)} — your motivational milestone!
                </Alert>
              )}

              {/* Per-debt payoff dates table */}
              <Typography variant="subtitle2" fontWeight={700} mt={3} mb={1}>
                Payoff schedule by debt
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Debt</strong></TableCell>
                    <TableCell align="right"><strong>Payoff date</strong></TableCell>
                    <TableCell align="right"><strong>Months</strong></TableCell>
                    <TableCell align="right"><strong>Total interest</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(plan.perDebt).map(([id, s]) => (
                    <TableRow key={id} hover>
                      <TableCell>{s.name}</TableCell>
                      <TableCell align="right">{fmtDate(s.payoffDate)}</TableCell>
                      <TableCell align="right">{s.payoffMonths}</TableCell>
                      <TableCell align="right">{fmt(s.totalInterest)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}

          {/* ─────────────────────────────────────────────────────────────────── */}
          {/* SECTION 3 — Strategy comparison (collapsible)                      */}
          {/* ─────────────────────────────────────────────────────────────────── */}
          <Divider sx={{ mb: 3 }} />

          <Button
            variant="outlined"
            onClick={handleCompare}
            endIcon={compareOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ mb: 2 }}
          >
            Compare strategies (Avalanche vs Snowball)
          </Button>

          <Collapse in={compareOpen}>
            {compareError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCompareError(null)}>
                {compareError}
              </Alert>
            )}

            {compareLoading && (
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
            )}

            {comparison && !compareLoading && comparison.hasDebts && (
              <Paper
                elevation={0}
                sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
              >
                {/* Top-level summary */}
                <Grid container spacing={2} mb={3}>
                  <Grid item xs={12} sm={4}>
                    <StatCard
                      label="Avalanche — total interest"
                      value={fmt(comparison.avalanche?.totalInterest)}
                      color="error"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <StatCard
                      label="Snowball — total interest"
                      value={fmt(comparison.snowball?.totalInterest)}
                      color="warning"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <StatCard
                      label="Saved by Avalanche"
                      value={fmt(comparison.savedByAvalanche)}
                      subLabel="vs Snowball"
                      color="success"
                      icon={<TrophyIcon />}
                    />
                  </Grid>
                </Grid>

                {/* Per-debt comparison table */}
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell><strong>Debt</strong></TableCell>
                      <TableCell align="center" colSpan={2}><strong>Avalanche</strong></TableCell>
                      <TableCell align="center" colSpan={2}><strong>Snowball</strong></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell />
                      <TableCell align="right"><em>Payoff date</em></TableCell>
                      <TableCell align="right"><em>Interest</em></TableCell>
                      <TableCell align="right"><em>Payoff date</em></TableCell>
                      <TableCell align="right"><em>Interest</em></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {comparison.avalanche &&
                      Object.entries(comparison.avalanche.perDebt).map(([id, av]) => {
                        const sn = comparison.snowball?.perDebt?.[id];
                        return (
                          <TableRow key={id} hover>
                            <TableCell>{av.name}</TableCell>
                            <TableCell align="right">{fmtDate(av.payoffDate)}</TableCell>
                            <TableCell align="right" sx={{ color: 'error.main' }}>
                              {fmt(av.totalInterest)}
                            </TableCell>
                            <TableCell align="right">
                              {sn ? fmtDate(sn.payoffDate) : '—'}
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'warning.main' }}>
                              {sn ? fmt(sn.totalInterest) : '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>

                {/* Method descriptions */}
                <Grid container spacing={2} mt={2}>
                  <Grid item xs={12} sm={6}>
                    <Alert severity="error" icon={<TrendingDownIcon />}>
                      <strong>Avalanche:</strong> {comparison.avalanche?.description}
                    </Alert>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Alert severity="success" icon={<TrophyIcon />}>
                      <strong>Snowball:</strong> {comparison.snowball?.description}
                    </Alert>
                  </Grid>
                </Grid>
              </Paper>
            )}
          </Collapse>
        </>
      )}
    </Box>
  );
};

export default DebtPayoffPage;
